import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

/**
 * HUB-493 severity tiers.
 *
 * - "error":   reserved lane rendered on top, sticky (duration: Infinity), never evicted
 *              by warning/success toasts. Lane cap 2 — a 3rd error replaces the OLDEST
 *              error (newest wins).
 * - "warning": 8s auto-dismiss. 409 conflict notices and success-with-warning messages.
 * - "success": 4s auto-dismiss. Everything else.
 *
 * Errors and ephemera (warning/success) each get a 2-slot window, so at most 4 toasts
 * are live at once. Before HUB-493, TOAST_LIMIT=1 meant ANY toast silently evicted any
 * other — a success firing right after an error destroyed the error before anyone read
 * it (the HUB-490 failure class).
 */
export type ToastSeverity = "error" | "warning" | "success"

const ERROR_LANE_LIMIT = 2
const EPHEMERAL_LANE_LIMIT = 2
// Delay before a closed toast is dropped from React state. This is the unmount delay
// after close (exit animation runs first), NOT the on-screen display time — display
// time is the per-toast `duration` handled by Radix.
const TOAST_REMOVE_DELAY = 1000000

export const SEVERITY_DURATIONS: Record<ToastSeverity, number> = {
  error: Infinity,
  warning: 8000,
  success: 4000,
}

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  severity: ToastSeverity
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

/**
 * HUB-522: severity is an explicit call-site declaration, no longer inferred from
 * toast wording. The HUB-493 conflict-vocabulary text heuristic (classifying a
 * destructive toast as a "warning" when its copy mentioned a conflict/refresh)
 * was always a bridge: copy is not a contract, and rewording a message silently
 * changed its display tier with no test failing at the call site. Every destructive
 * call site now declares `severity` itself (409 conflict notices → "warning",
 * real failures → "error"). Guarded by toast-severity-discipline.test.ts.
 */
export function classifySeverity(input: {
  severity?: ToastSeverity
  variant?: ToastProps["variant"]
}): ToastSeverity {
  // Explicit severity from the caller always wins.
  if (input.severity) return input.severity
  // FAIL-SAFE (HUB-493 contract, kept by HUB-522): a destructive toast that does
  // not declare a severity lands in the sticky error tier, never an ephemeral one.
  if (input.variant === "destructive") return "error"
  return "success"
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

/**
 * HUB-493: gracefully retire a toast that lost its lane slot. Flip open:false so the
 * exit animation runs (instead of the old silent slice() truncation that made toasts
 * vanish mid-read), clear any stale remove-queue timer for it (the pre-HUB-493 timer
 * leak: displaced toasts left orphaned entries in toastTimeouts), and schedule its
 * removal from state.
 */
const evictToast = (toastId: string) => {
  const existing = toastTimeouts.get(toastId)
  if (existing) {
    clearTimeout(existing)
    toastTimeouts.delete(toastId)
  }
  addToRemoveQueue(toastId)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      const next = [action.toast, ...state.toasts]
      // Only OPEN toasts compete for lane slots; toasts already animating out
      // (open === false) are pass-through and must not evict a live toast.
      const live = next.filter((t) => t.open !== false)
      const errors = live.filter((t) => t.severity === "error")
      const ephemera = live.filter((t) => t.severity !== "error")
      // Newest-first within each lane: a 3rd error replaces the OLDEST error.
      const keptIds = new Set(
        [
          ...errors.slice(0, ERROR_LANE_LIMIT),
          ...ephemera.slice(0, EPHEMERAL_LANE_LIMIT),
        ].map((t) => t.id),
      )
      const withEvictions = next.map((t) => {
        if (t.open === false || keptIds.has(t.id)) return t
        // ! Side effect ! — mirrors the DISMISS_TOAST precedent below.
        evictToast(t.id)
        return { ...t, open: false }
      })
      return {
        ...state,
        // Errors render first: array order controls stacking, so the error lane
        // always sits on top of the ephemeral lane.
        toasts: [
          ...withEvictions.filter((t) => t.severity === "error"),
          ...withEvictions.filter((t) => t.severity !== "error"),
        ],
      }
    }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

// Test-only escape hatch (HUB-493 unit tests): reading the module state directly
// beats exporting dispatch/listeners and inviting production misuse.
export function getToastsSnapshot(): ToasterToast[] {
  return memoryState.toasts
}

type Toast = Omit<ToasterToast, "id" | "severity"> & {
  severity?: ToastSeverity
}

function toast({ ...props }: Toast) {
  const id = genId()

  const severity = classifySeverity(props)
  // Severity decides the display time unless the caller passed an explicit duration
  // (e.g. team.tsx shows a temporary password for 30s).
  const duration = props.duration ?? SEVERITY_DURATIONS[severity]

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      severity,
      duration,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
